import React from "react";
import InspectionRequestPreferences from "../utils/InspectionRequestPreferences";
import InspectionTypeScheduleCalendarUtils from "../utils/InspectionTypeScheduleCalendarUtils";
import useInspection from "../../../../hooks/inspections/useInspection";
import { CalendarDate, parseDate } from "@internationalized/date";
import { formatDate } from "date-fns";
import classNames from "classnames";

interface IInspectionTypeScheduleCalendarHeader {
  date: CalendarDate;
  inspectionId?: string;
}
interface Preference {
  date: string;
  meridiem: "AM" | "PM" | null;
}
export default function InspectionTypeScheduleCalendarHeader({
  date,
  inspectionId,
}: IInspectionTypeScheduleCalendarHeader) {
  const { inspectionQuery } = useInspection({ id: inspectionId ?? "", enabled: !!inspectionId });

  const request = inspectionQuery.data?.data.attributes.request;
  const preferredDates =
    request?.preferences
      .slice(0, 3)
      .filter((preference): preference is Preference => {
        return !!preference.date;
      })
      .map((preference) => {
        return {
          date: parseDate(formatDate(new Date(preference.date), "yyyy-MM-dd")),
          meridiem: preference.meridiem,
        };
      }) ?? [];

  const weekdays = InspectionTypeScheduleCalendarUtils.getWeekDays(date);

  const getPreferredStyles = (date: CalendarDate, preferences: InspectionRequestPreferences) => {
    if (preferences.isFirstPreference(date)) {
      return "tw-bg-orange tw-text-white";
    }
    if (preferences.isSecondPreference(date)) {
      return "tw-bg-blue tw-text-white";
    }
    if (preferences.isThirdPreference(date)) {
      return "tw-bg-[#767676] tw-text-white";
    }

    return "";
  };

  return (
    <div className='tw-grid tw-grid-cols-[repeat(8,minmax(75.73px,1fr))]'>
      {/* Extra div for a blank column/cell in the grid */}
      <div />
      {weekdays.map((dayOfWeek, index) => {
        const preferences = new InspectionRequestPreferences(preferredDates);
        const preferredStyles = getPreferredStyles(dayOfWeek, preferences);
        const pmPref = preferences.hasPMPreference(dayOfWeek);
        const amPref = preferences.hasAMPreference(dayOfWeek);

        return (
          <div
            key={dayOfWeek.toString()}
            className={classNames({
              "tw-border-[1px] tw-border-y-0 tw-border-solid tw-border-[#dededf] tw-text-center tw-font-bold": true,
              "tw-border-l-0": true,
              "tw-border-r-0": index >= weekdays.length - 1 || index < 0,
            })}
          >
            <div className='tw-mb-0.5'>{InspectionTypeScheduleCalendarUtils.getWeekDayHeaderText(dayOfWeek)}</div>
            <div>
              <div className='tw-grid tw-grid-cols-2 tw-font-normal'>
                <div className='tw-px-1.5'>
                  <div className={`tw-rounded-lg tw-p-0.5 ${amPref ? preferredStyles : ""}`}>AM</div>
                </div>
                <div className='tw-px-1.5'>
                  <div className={`tw-rounded-lg tw-p-0.5 ${pmPref ? preferredStyles : ""}`}>PM</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
